import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PageBanner from '../components/PageBanner';

const TESTIMONIAL_VIDEOS = [
  { id: 'ifnQG672SX8', title: 'Client Testimonial 1', start: 1 },
  { id: 'Umiiud-P4hc', title: 'Client Testimonial 2' },
  { id: '7F-eixoyYpM', title: 'Client Testimonial 3' },
  { id: 'DAd66UNC2pc', title: 'Client Testimonial 4' },
];

const CLIENT_VIDEOS = [
  { src: '/assets/testimonials/testimonial-v11.mp4', title: 'Client Experience 1' },
  { src: '/assets/testimonials/testimonial-v12.mp4', title: 'Client Experience 2' },
  { src: '/assets/testimonials/testimonial-v13.mp4', title: 'Client Experience 3' },
];

const TRAINEE_VIDEOS = [
  { src: '/assets/testimonials/trainees/trainee-1.mp4', title: 'Trainee Testimonial 1' },
  { src: '/assets/testimonials/trainees/trainee-2.mp4', title: 'Trainee Feedback' },
  { src: '/assets/testimonials/trainees/trainee-3.mp4', title: 'Trainee Testimonial 2' },
  { src: '/assets/testimonials/trainees/trainee-4.mp4', title: 'Trainee Testimonial 3' },
];

const VideoCard = ({ src, title }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      video.pause();
    } else {
      video.play();
    }
    setPlaying(!playing);
  };

  return (
    <div className="client-video-card" onClick={handlePlay}>
      <video
        ref={videoRef}
        src={src}
        title={title}
        playsInline
        preload="metadata"
        onEnded={() => setPlaying(false)}
      />
      {!playing && (
        <div className="client-video-card__overlay">
          <div className="client-video-card__play">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <polygon points="6,3 20,12 6,21" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

function TestimonialsPage() {
  const location = useLocation();
  const [tab, setTab] = useState('client');

  // Sync tab to hash on mount + when hash changes (header dropdown deep-links)
  useEffect(() => {
    if (location.hash === '#trainee') setTab('trainee');
    else if (location.hash === '#client') setTab('client');
  }, [location.hash]);

  return (
    <>
      <PageBanner title="Testimonials" />

      {/* Floating tab bar — anchored over the bottom of the banner so it feels
          like one continuous "page header" instead of a stray white strip. */}
      <div className="testimonials-tabbar">
        <div className="container">
          <div className="testimonials-tabs" role="tablist" aria-label="Testimonial types">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'client'}
              className={`testimonials-tab ${tab === 'client' ? 'active' : ''}`}
              onClick={() => setTab('client')}
            >
              Client Testimonials
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'trainee'}
              className={`testimonials-tab ${tab === 'trainee' ? 'active' : ''}`}
              onClick={() => setTab('trainee')}
            >
              Trainee Testimonials
            </button>
          </div>
          <p className="testimonials-tabbar__desc">
            {tab === 'trainee'
              ? 'Real feedback from trainees who completed our programs at MindSpa.'
              : 'Real stories from people whose lives have been transformed through our therapeutic services.'}
          </p>
        </div>
      </div>

      {tab === 'trainee' ? (
        <section className="testimonials-content testimonials-content--cream">
          <div className="container">
            <div className="client-videos-grid">
              {TRAINEE_VIDEOS.map((video, i) => (
                <VideoCard key={i} src={video.src} title={video.title} />
              ))}
            </div>
          </div>
        </section>
      ) : (
        <>
          <section id="training-testimonials" className="testimonials-content">
            <div className="container">
              <div className="testimonials-page-grid">
                {TESTIMONIAL_VIDEOS.map((video) => (
                  <div key={video.id} className="testimonial-video-card">
                    <div className="testimonial-video-wrapper">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}${video.start ? `?start=${video.start}` : ''}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="testimonials-content testimonials-content--cream">
            <div className="container">
              <div className="testimonials-subhead">
                <span className="testimonials-subhead__line" aria-hidden="true" />
                <h2 className="testimonials-subhead__title">Vertical video stories</h2>
                <span className="testimonials-subhead__line" aria-hidden="true" />
              </div>
              <div className="client-videos-grid">
                {CLIENT_VIDEOS.map((video, i) => (
                  <VideoCard key={i} src={video.src} title={video.title} />
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <style>{`
        /* === Floating tab bar (overlaps bottom of PageBanner) === */
        .testimonials-tabbar {
          position: relative;
          margin-top: -32px;
          z-index: 5;
          padding-bottom: 36px;
        }
        .testimonials-tabs {
          display: inline-flex;
          gap: 4px;
          padding: 6px;
          background: #fff;
          border: 1px solid var(--border-light, #E8E3D6);
          border-radius: 999px;
          box-shadow: 0 14px 36px -12px rgba(0, 0, 0, 0.18),
                      0 2px 6px rgba(0, 0, 0, 0.04);
          margin: 0 auto;
        }
        /* Wrap tabs centered horizontally without losing the inline-flex shape */
        .testimonials-tabbar .container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .testimonials-tab {
          padding: 11px 28px;
          border: 0;
          background: transparent;
          color: var(--text-light, #8A8F92);
          font-family: 'Inter', sans-serif;
          font-size: 14.5px;
          font-weight: 600;
          letter-spacing: 0.2px;
          border-radius: 999px;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
          white-space: nowrap;
        }
        .testimonials-tab:hover {
          color: var(--text-heading, #1B2225);
          background: var(--bg-section, #FAF6EC);
        }
        .testimonials-tab.active {
          background: var(--accent, #3FA9B5);
          color: #fff;
          box-shadow: 0 4px 14px rgba(63, 169, 181, 0.35);
        }
        .testimonials-tab.active:hover {
          color: #fff;
        }
        .testimonials-tabbar__desc {
          margin: 22px auto 0;
          max-width: 620px;
          text-align: center;
          font-size: 15.5px;
          color: var(--text-body, #4B5358);
          line-height: 1.6;
        }

        /* === Content sections === */
        .testimonials-content {
          padding: 28px 0 80px;
          background: #fff;
        }
        .testimonials-content--cream {
          background: var(--bg-section, #FAF6EC);
          padding: 56px 0 80px;
        }

        /* Subhead used only when a tab has multiple sub-sections (e.g. client → vertical videos) */
        .testimonials-subhead {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 0 auto 32px;
          max-width: 540px;
        }
        .testimonials-subhead__line {
          flex: 1;
          height: 1px;
          background: var(--border-light, #E8E3D6);
        }
        .testimonials-subhead__title {
          font-family: 'Lora', Georgia, serif;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-heading, #1B2225);
          letter-spacing: 0.2px;
          margin: 0;
          text-align: center;
        }

        /* === YouTube grid === */
        .testimonials-page-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 30px;
        }
        .testimonial-video-card {
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          background: #000;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .testimonial-video-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }
        .testimonial-video-wrapper {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
        }
        .testimonial-video-wrapper iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        /* === Vertical client/trainee video grid === */
        .client-videos-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .client-video-card {
          position: relative;
          border-radius: 14px;
          overflow: hidden;
          background: #000;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          cursor: pointer;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
          aspect-ratio: 9 / 16;
        }
        .client-video-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 36px rgba(0, 0, 0, 0.18);
        }
        .client-video-card video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .client-video-card__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.45) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s ease;
        }
        .client-video-card:hover .client-video-card__overlay {
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.55) 100%);
        }
        .client-video-card__play {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 24px rgba(63, 169, 181, 0.4);
          transition: transform 0.3s ease;
        }
        .client-video-card__play svg { margin-left: 3px; }
        .client-video-card:hover .client-video-card__play { transform: scale(1.1); }

        @media (max-width: 900px) {
          .client-videos-grid { grid-template-columns: repeat(2, 1fr); }
          .testimonials-tabbar { margin-top: -28px; }
          .testimonials-tabs { padding: 5px; }
          .testimonials-tab { padding: 10px 22px; font-size: 14px; }
        }
        @media (max-width: 600px) {
          .testimonials-page-grid { grid-template-columns: 1fr; }
          .client-videos-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .client-video-card__play { width: 50px; height: 50px; }
          .client-video-card__play svg { width: 22px; height: 22px; }
          .testimonials-tabbar { margin-top: -24px; padding-bottom: 28px; }
          .testimonials-tabs { width: 100%; max-width: 360px; }
          .testimonials-tab { flex: 1; padding: 10px 14px; }
          .testimonials-tabbar__desc { font-size: 14.5px; padding: 0 16px; margin-top: 18px; }
          .testimonials-content { padding: 18px 0 60px; }
          .testimonials-content--cream { padding: 40px 0 60px; }
        }
      `}</style>
    </>
  );
}

export default TestimonialsPage;
